import type { ProjectState, RoomSeparator, Space, SpaceType, Wall } from '../types';
import { boundarySegments, buildFaces, resolveSpaces, type ResolvedSpace, type RoomFace } from './geometry';

// ── Types de pieces ────────────────────────────────────────

export const SPACE_TYPES: { type: SpaceType; label: string; color: string; numbered: boolean }[] = [
  { type: 'chambre', label: 'Chambre', color: '#dbeafe', numbered: true },
  { type: 'sejour', label: 'Séjour', color: '#fef3c7', numbered: false },
  { type: 'salon', label: 'Salon', color: '#fef3c7', numbered: false },
  { type: 'salle_a_manger', label: 'Salle à manger', color: '#fde68a', numbered: false },
  { type: 'cuisine', label: 'Cuisine', color: '#fed7aa', numbered: false },
  { type: 'salle_de_bain', label: 'Salle de bain', color: '#cffafe', numbered: true },
  { type: 'wc', label: 'WC', color: '#e0f2fe', numbered: true },
  { type: 'entree', label: 'Entrée', color: '#ede9fe', numbered: false },
  { type: 'couloir', label: 'Couloir', color: '#f3e8ff', numbered: true },
  { type: 'bureau', label: 'Bureau', color: '#dcfce7', numbered: true },
  { type: 'buanderie', label: 'Buanderie', color: '#e2e8f0', numbered: false },
  { type: 'cellier', label: 'Cellier', color: '#e7e5e4', numbered: false },
  { type: 'dressing', label: 'Dressing', color: '#fce7f3', numbered: true },
  { type: 'garage', label: 'Garage', color: '#e5e7eb', numbered: true },
  { type: 'autre', label: 'Pièce', color: '#f1f5f9', numbered: true },
];

/** Pieces qui ne comptent pas dans la surface habitable (annexes : garage) */
const NON_HABITABLE: SpaceType[] = ['garage'];
export const isHabitable = (type: SpaceType) => !NON_HABITABLE.includes(type);

export function spaceTypeInfo(type: SpaceType) {
  return SPACE_TYPES.find(t => t.type === type) ?? SPACE_TYPES[SPACE_TYPES.length - 1];
}

/** Nom propose : « Chambre 2 » si d'autres chambres existent, « Cuisine » pour une piece unique */
export function suggestSpaceName(type: SpaceType, spaces: Space[]): string {
  const info = spaceTypeInfo(type);
  const same = spaces.filter(s => s.type === type).length;
  if (!info.numbered && same === 0) return info.label;
  return `${info.label} ${same + 1}`;
}

/** Numero suivant (numerotation Revit : 1, 2, 3…) */
export function nextSpaceNumber(spaces: Space[]): string {
  const max = spaces.reduce((m, s) => Math.max(m, parseInt(s.number, 10) || 0), 0);
  return String(max + 1);
}

// ── Modele de pieces d'un etage ────────────────────────────

/** Pixels du plan pour une longueur en cm (meme convention que le canvas) */
export function cmToPlanPx(cm: number, cal?: ProjectState['calibration']): number {
  return cal ? (cm * 10) / (cal.realDistance / cal.pixelDistance) : cm;
}

/** Metres carres pour une surface en px² du plan */
export function planPx2ToM2(px2: number, cal?: ProjectState['calibration']): number {
  const k = cal ? cal.realDistance / cal.pixelDistance / 1000 : 0.01;
  return px2 * k * k;
}

/** Metres pour une longueur en px du plan */
export function planPxToM(px: number, cal?: ProjectState['calibration']): number {
  return px * (cal ? cal.realDistance / cal.pixelDistance / 1000 : 0.01);
}

export interface RoomModel {
  faces: RoomFace[];
  resolved: ResolvedSpace[];
}

/** Espaces fermes et pieces d'un etage (elements deja filtres sur l'etage) */
export function computeRoomModel(
  walls: Wall[], separators: RoomSeparator[], spaces: Space[], cal?: ProjectState['calibration'],
): RoomModel {
  const faces = buildFaces(boundarySegments(walls, separators, cm => cmToPlanPx(cm, cal)));
  return { faces, resolved: resolveSpaces(spaces, faces) };
}

/**
 * Fusion des pieces redondantes : quand un element delimitant disparait (mur supprime, separation
 * supprimee, mur qui ne delimite plus), deux pieces peuvent se retrouver dans le meme espace.
 * On garde celle de plus petit numero et on supprime les autres. Les pieces non fermees sont conservees.
 * Renvoie la nouvelle liste, ou null si rien ne change.
 */
export function mergeRedundantSpaces(project: Pick<ProjectState, 'walls' | 'roomSeparators' | 'spaces' | 'calibration'>): Space[] | null {
  const spaces = project.spaces || [];
  if (spaces.length < 2) return null;
  const removed = new Set<string>();
  const floorIds = new Set(spaces.map(sp => sp.floorId ?? ''));
  for (const floorId of floorIds) {
    const onFloor = spaces
      .filter(sp => (sp.floorId ?? '') === floorId)
      .sort((a, b) => (parseInt(a.number, 10) || 1e9) - (parseInt(b.number, 10) || 1e9));
    if (onFloor.length < 2) continue;
    const model = computeRoomModel(
      project.walls.filter(w => (w.floorId ?? '') === floorId),
      (project.roomSeparators || []).filter(r => (r.floorId ?? '') === floorId),
      onFloor,
      project.calibration,
    );
    for (const r of model.resolved) if (r.state === 'redondante') removed.add(r.space.id);
  }
  return removed.size ? spaces.filter(sp => !removed.has(sp.id)) : null;
}
